"use client";

import React from "react";
import RichTextInput from "@/components/ui/RichTextInput";
import { ListEditor } from "../../editors/ListEditor";
import { FaqEditor } from "../../editors/FaqEditor";
import { ReferenceEditor } from "../../editors/ReferenceEditor";
import { ContentReferenceEditor } from "../../editors/ContentReferenceEditor";
import { ProductCardEditor } from "../../editors/ProductCardEditor";
import { ContactBlockEditor } from "../../editors/ContactBlockEditor";
import { ContentLinkPicker } from "@/components/ui/ContentLinkPicker";
import { TableEditorModal } from "../../editors/TableEditor";
import { BlockCardInlineControls } from "./BlockCardInlineControls";
import { TableBlockData } from "@/cms/blocks/table";

type Props = {
  block: any;
  locale: "en" | "fa";
  localeStrings: any;
  inputDir: "ltr" | "rtl";
  inputAlignment: string;
  localizedInputClass: string;
  onChange: (patch: (data: any) => any) => void;
  onChangeMeta?: (patch: (meta: any) => any) => void;
  onRequestConfirm: any;
  inline: {
    show: boolean;
    enabled: boolean;
    align: "left" | "right";
    update: (enabled: boolean, align?: "left" | "right") => void;
  };
  shortDescription: {
    value: boolean;
    inUse: boolean;
  };
  image: {
    isBlock: boolean;
    isHero: boolean;
    heroInUse: boolean;
    data: any;
    display: {
      previewSrc?: string;
      name?: string;
      width?: number;
      height?: number;
    };
    onPick: () => void;
    clearImage: () => void;
  };
  table: {
    data: TableBlockData | null;
    stats: any;
    editorOpen: boolean;
    setEditorOpen: (open: boolean) => void;
  };
  linkPicker: {
    open: boolean;
    setOpen: (open: boolean) => void;
  };
};

export function BlockCardBody(props: Props) {
  const {
    block,
    locale,
    localeStrings,
    inputDir,
    inputAlignment,
  localizedInputClass,
  onChange,
  onRequestConfirm,
  inline,
  shortDescription,
  image,
  table,
  linkPicker,
} = props;

  const isWhitespaceDivider = block.type === "divider" ? (((block.data as any).variant ?? "line") === "space") : false;
  const quoteVariant = block.type === "quote" ? ((block.data as any).variant ?? "default") : "default";
  const headingRef = React.useRef<HTMLTextAreaElement | null>(null);

  const autoResizeHeading = React.useCallback((element: HTMLTextAreaElement | null) => {
    if (!element) return;
    element.style.height = "0px";
    element.style.height = `${element.scrollHeight}px`;
  }, []);

  React.useEffect(() => {
    if (block.type !== "heading") return;
    autoResizeHeading(headingRef.current);
  }, [block, autoResizeHeading]);

  const bodyContent = (
    <div className="space-y-3">
      {block.type === "heading" && (
        <div className="flex gap-2">
          <select
            value={(block.data as any).level ?? 2}
          onChange={(event) =>
            onChange((data) => ({
              ...data,
              level: Number(event.target.value),
            }))
          }
          className="rounded border bg-white px-2 py-1 focus:outline-none"
        >
            {[1, 2, 3].map((level) => (
              <option key={level} value={level} style={{ fontSize: `${1.75 - (level - 1) * 0.25}rem`, fontWeight: level === 1 ? 700 : 600 }}>
                H{level}
              </option>
            ))}
          </select>
          <textarea
            dir={inputDir}
            rows={1}
            className={`flex-1 resize-none overflow-hidden ${localizedInputClass}`}
            placeholder={localeStrings.placeholders.heading}
            value={(block.data as any).text || ""}
            onChange={(event) => {
              onChange((data) => ({ ...data, text: event.target.value }));
              autoResizeHeading(event.currentTarget);
            }}
            ref={(element) => {
              headingRef.current = element;
              autoResizeHeading(element);
            }}
          />
        </div>
      )}

      {block.type === "paragraph" && (
        <>
        <RichTextInput
          value={(block.data as any)?.richText ?? { type: "doc", content: [{ type: "paragraph", content: [{ type: "text", text: "" }] }] }}
          onChange={({ json }) => onChange((data) => ({ ...data, richText: json }))}
          placeholder={localeStrings.placeholders.paragraph}
          className="focus-within:border-pinkCustom space-y-2 rounded border border-gray-300"
          editorClassName={inputAlignment}
          minHeightClassName="min-h-16"
          dir={inputDir as "ltr" | "rtl"}
        />
        <label className="inline-flex items-center gap-2 text-sm text-gray-600">
          <input
            type="checkbox"
            className="h-4 w-4"
            checked={shortDescription.value}
            disabled={shortDescription.inUse && !shortDescription.value}
            onChange={(event) =>
              onChange((data) => ({
                ...data,
                variant: event.target.checked ? "shortDescription" : "default",
              }))
              }
            />
            <span>{localeStrings.helpers.shortDesc}</span>
          </label>
        </>
      )}

      {block.type === "image" && (
        <div className="space-y-3">
          <input
            dir={inputDir}
            className={`w-full ${localizedInputClass}`}
            placeholder={localeStrings.placeholders.imageAlt}
            value={(block.data as any).alt || ""}
            onChange={(event) => onChange((data) => ({ ...data, alt: event.target.value }))}
          />
          {image.display.previewSrc ? (
            <div className="overflow-hidden rounded border border-gray-200 bg-white">
              <div className="flex items-center justify-center bg-gray-100">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={image.display.previewSrc} alt="" className="max-h-40 w-full object-contain" loading="lazy" />
              </div>
              <div className="flex flex-wrap items-center gap-3 px-3 py-2 text-xs text-gray-600">
                <div className="min-w-0">
                  {image.display.name ? <div className="truncate font-medium">{image.display.name}</div> : <div className="font-medium text-gray-500">{localeStrings.helpers.selectedImage}</div>}
                  <div className="text-gray-500">{image.display.width && image.display.height ? `${image.display.width} x ${image.display.height}px` : "Size unavailable"}</div>
                </div>
                <div className="ml-auto flex gap-2">
                  <button
                    type="button"
                    onClick={image.onPick}
                    className="rounded border border-gray-300 px-2 py-1 text-[11px] font-medium tracking-wide text-gray-700 uppercase hover:bg-gray-100"
                  >
                    {localeStrings.helpers.changeImage}
                  </button>
                  <button
                    type="button"
                  onClick={image.clearImage}
                    className="rounded border border-red-200 px-2 py-1 text-[11px] font-medium tracking-wide text-red-600 uppercase hover:bg-red-50"
                  >
                    {localeStrings.helpers.clearImage}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={image.onPick}
              className="text-slateCustom flex w-full flex-col items-center justify-center gap-2 rounded border border-gray-300 bg-white px-4 py-10 text-sm font-medium transition"
            >
              <span>{localeStrings.helpers.selectImage}</span>
            </button>
          )}

          <label className="inline-flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={image.isHero}
              disabled={image.heroInUse && !image.isHero}
              onChange={(event) =>
                onChange((data) => ({
                  ...data,
                  variant: event.target.checked ? "hero" : "default",
                }))
              }
            />
            <span>{localeStrings.helpers.heroToggle}</span>
          </label>
          {inline.show ? (
            <BlockCardInlineControls
              inlineEnabled={inline.enabled}
              inlineAlign={inline.align}
              onToggleInline={inline.update}
              labels={{
                inlineLabel: localeStrings.helpers.inlineLabel,
                inlineLeft: localeStrings.helpers.inlineLeft,
                inlineRight: localeStrings.helpers.inlineRight,
              }}
            />
          ) : null}
        </div>
      )}

      {block.type === "divider" && (
        <div className="space-y-2">
          <div className="py-2">{isWhitespaceDivider ? <div className="h-6 rounded border border-dashed border-gray-200 bg-gray-50" /> : <hr />}</div>
          <label className="inline-flex items-center gap-2 text-sm text-gray-600">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={isWhitespaceDivider}
              onChange={(event) =>
                onChange((data) => ({
                  ...data,
                  variant: event.target.checked ? "space" : "line",
                }))
              }
            />
            <span>{localeStrings.helpers.dividerSpace}</span>
          </label>
        </div>
      )}

      {block.type === "list" && (
        <ListEditor block={block} onChange={onChange} onRequestConfirm={onRequestConfirm} locale={locale} />
      )}

      {block.type === "faq" && (
        <FaqEditor block={block} locale={locale} onChange={onChange} onRequestConfirm={onRequestConfirm} />
      )}

      {block.type === "quote" && (
        <div className="space-y-3">
          <textarea
            dir={inputDir}
            className={`w-full ${localizedInputClass}`}
            placeholder={localeStrings.placeholders.quote}
            value={(block.data as any).text || ""}
            onChange={(event) => onChange((data) => ({ ...data, text: event.target.value }))}
          />
          <input
            dir={inputDir}
            className={`w-full ${localizedInputClass}`}
            placeholder={localeStrings.placeholders.cite}
            value={(block.data as any).cite || ""}
            onChange={(event) => onChange((data) => ({ ...data, cite: event.target.value }))}
          />
          <label className="inline-flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              className="h-4 w-4"
              checked={quoteVariant === "bracket"}
              onChange={(event) =>
                onChange((data) => ({
                  ...data,
                  variant: event.target.checked ? "bracket" : "default",
                }))
              }
            />
            <span>Bracket style</span>
          </label>
          {quoteVariant === "bracket" && inline.show ? (
            <BlockCardInlineControls
              inlineEnabled={inline.enabled}
              inlineAlign={inline.align}
              onToggleInline={inline.update}
              labels={{
                inlineLabel: localeStrings.helpers.inlineLabel,
                inlineLeft: localeStrings.helpers.inlineLeft,
                inlineRight: localeStrings.helpers.inlineRight,
              }}
            />
          ) : null}
        </div>
      )}

      {block.type === "link" && (
        <div className="space-y-3">
          {(() => {
            const linkData = block.data as any;
            const linkVariant: "default" | "cta" =
              linkData.variant === "cta" ? "cta" : "default";
            const linkMode: "url" | "content" =
              linkVariant === "cta"
                ? "url"
                : linkData.mode === "content"
                  ? "content"
                  : "url";
            const setMode = (mode: "url" | "content") =>
              onChange((data) => ({
                ...data,
                variant: linkVariant,
                mode: mode,
                ...(mode === "url"
                  ? { url: data.url ?? "", contentSlug: "", contentId: "" }
                  : {
                    contentSlug: data.contentSlug ?? "",
                    contentId: data.contentId ?? "",
                    url: "",
                  }),
              }));

            return (
              <>
              <input
                dir={inputDir}
                className={`w-full ${localizedInputClass}`}
                placeholder={localeStrings.placeholders.linkLabel}
                value={(block.data as any).title || ""}
                onChange={(event) => onChange((data) => ({ ...data, title: event.target.value }))}
              />
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={linkVariant === "cta"}
                  onChange={(event) =>
                    onChange((data) => ({
                      ...data,
                      variant: event.target.checked ? "cta" : "default",
                      mode: "url",
                  ...(event.target.checked ? { contentSlug: "", contentId: "" } : {}),
                }))
              }
                />
                <span>CTA style</span>
              </label>
              {linkVariant !== "cta" ? (
                <div className="flex flex-wrap items-center gap-2 text-sm text-gray-700">
                  <span className="font-medium">Link type:</span>
                  <div className="inline-flex overflow-hidden rounded border border-gray-200">
                    <button
                      type="button"
                      className={`px-3 py-1 text-sm font-medium ${linkMode === "url" ? "bg-pinkCustom text-white" : "bg-white text-gray-700"}`}
                      onClick={() => setMode("url")}
                    >
                      External URL
                    </button>
                    <button
                      type="button"
                      className={`px-3 py-1 text-sm font-medium border-l border-gray-200 ${linkMode === "content" ? "bg-pinkCustom text-white" : "bg-white text-gray-700"}`}
                      onClick={() => setMode("content")}
                    >
                      Content link
                    </button>
                  </div>
                </div>
              ) : null}

              {linkMode === "content" ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
                      {(block.data as any).contentSlug ? (block.data as any).contentSlug : "No content selected"}
                    </div>
                    <button
                      type="button"
                      className="rounded border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
                      onClick={() => {
                        setMode("content");
                        linkPicker.setOpen(true);
                      }}
                    >
                      Pick
                    </button>
                    {(block.data as any).contentSlug ? (
                      <button
                        type="button"
                        className="rounded border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                        onClick={() =>
                          onChange((data) => ({
                            ...data,
                            contentSlug: "",
                            contentId: "",
                            mode: "content",
                          }))
                        }
                      >
                        Clear
                      </button>
                    ) : null}
                  </div>
                </div>
              ) : (
                <input
                  dir={inputDir}
                  className={`w-full ${localizedInputClass}`}
                  placeholder={localeStrings.placeholders.linkHref}
                  value={(block.data as any).url || ""}
                  onChange={(event) => onChange((data) => ({ ...data, url: event.target.value, mode: "url" }))}
                />
              )}
            </>
            );
          })()}
        </div>
      )}

      {block.type === "embed" && (
        <div className="space-y-2">
          <input
            dir={inputDir}
            className={`w-full ${localizedInputClass}`}
            placeholder={localeStrings.placeholders.embedUrl}
            value={(block.data as any).url || ""}
            onChange={(event) => onChange((data) => ({ ...data, url: event.target.value }))}
          />
        </div>
      )}

      {block.type === "reference" && <ReferenceEditor block={block} locale={locale} onChange={onChange} />}

      {block.type === "contentReference" && <ContentReferenceEditor block={block} onChange={onChange} locale={locale} />}

      {block.type === "productCard" && <ProductCardEditor block={block} onChange={onChange} locale={locale} />}

      {block.type === "contact" && (
        <ContactBlockEditor block={block} onChange={onChange} locale={locale} />
      )}

      {table.data && (
        <>
          <div className="space-y-2 rounded border border-gray-200 bg-white p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="text-sm text-gray-700">
                {table.stats ? (
                  <>
                    <span className="text-gray-500">
                      {table.stats.rows} rows  {table.stats.cols} columns
                    </span>
                  </>
                ) : (
                  <span className="text-amber-700">Table data needs attention</span>
                )}
              </div>
              <button
                type="button"
                onClick={() => table.setEditorOpen(true)}
                className="rounded border border-pinkCustom px-3 py-1.5 text-xs font-semibold text-pinkCustom transition hover:bg-pinkCustom/10"
              >
                Edit table
              </button>
            </div>
            {table.data.caption ? (
              <p className="text-xs text-gray-600">Caption: {table.data.caption}</p>
            ): null}
          </div>
          {table.editorOpen ? (
            <TableEditorModal
              open={table.editorOpen}
              onOpenChange={table.setEditorOpen}
              data={table.data}
              onChange={(updater) => onChange((prev) => updater((prev as unknown as TableBlockData) ?? table.data))}
            />
          ) : null}
        </>
      )}
    </div>
  );

  return (
    <>
      {bodyContent}
      {block.type === "link" ? (
        <ContentLinkPicker
          open={linkPicker.open}
          onClose={() => linkPicker.setOpen(false)}
          initialSlug={(block.data as any).contentSlug}
          onSelect={({ contentSlug, contentId }) => {
            onChange((data) => ({
              ...data,
              mode: "content",
              contentSlug,
              contentId: contentId ?? "",
            }));
            linkPicker.setOpen(false);
          }}
        />
      ) : null}
    </>
  );
}
